import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Icon } from '../components/icons';
import { config } from '../config';

interface DemoAccount {
  subject: string;
  displayName: string;
  description: string;
  identifiant: string;
  motDePasse: string;
  doubleAuthentification: boolean;
}

type LoginStep =
  | { etape: 'connecte'; accessToken: string }
  | { etape: 'double_authentification'; defi: string; codeDemo: string };

export function LoginPage() {
  const { status, acceptDevToken, loginOidc } = useAuth();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<DemoAccount[]>([]);
  const [identifiant, setIdentifiant] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [challenge, setChallenge] = useState<{ defi: string; codeDemo: string } | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (config.authMode !== 'dev') return;
    api<DemoAccount[]>('/dev/comptes')
      .then(setAccounts)
      .catch((e: ApiError) => setError(e.message));
  }, []);

  if (status === 'authenticated') return <Navigate to="/espace" replace />;

  if (config.authMode === 'oidc') {
    return (
      <div className="card stack" style={{ maxWidth: 520 }}>
        <h1>Connexion</h1>
        <p>Vous allez être redirigé vers le service d’authentification de l’administration.</p>
        <button className="btn" type="button" onClick={() => void loginOidc()}>
          Se connecter
        </button>
      </div>
    );
  }

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Connexion impossible.');
    } finally {
      setBusy(false);
    }
  };

  const finish = async (accessToken: string) => {
    await acceptDevToken(accessToken);
    navigate('/espace');
  };

  const submitPassword = (e: React.FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const step = await api<LoginStep>('/dev/connexion', {
        method: 'POST',
        body: { identifiant, motDePasse },
      });
      if (step.etape === 'connecte') await finish(step.accessToken);
      else {
        setChallenge({ defi: step.defi, codeDemo: step.codeDemo });
        setCode('');
      }
    });
  };

  const submitCode = (e: React.FormEvent) => {
    e.preventDefault();
    if (!challenge) return;
    void run(async () => {
      try {
        const step = await api<{ accessToken: string }>('/dev/connexion/double-authentification', {
          method: 'POST',
          body: { defi: challenge.defi, code },
        });
        await finish(step.accessToken);
      } catch (err) {
        // Défi consommé ou expiré : retour à l'étape du mot de passe.
        setChallenge(null);
        throw err;
      }
    });
  };

  const choose = (a: DemoAccount) => {
    setChallenge(null);
    setError(null);
    setIdentifiant(a.identifiant);
    setMotDePasse(a.motDePasse);
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <div className="stack" style={{ gap: 40 }}>
      <div className="login">
        {challenge ? (
          <form className="card stack" onSubmit={submitCode} ref={formRef}>
            <div>
              <p className="eyebrow">Connexion · étape 2 sur 2</p>
              <h1 style={{ marginBottom: 0 }}>Double authentification</h1>
            </div>
            <p className="muted" style={{ margin: 0 }}>
              Saisissez le code à six chiffres de votre application d’authentification.
            </p>
            <div className="field">
              <label htmlFor="mfa-code">Code de vérification</label>
              <div className="inline-field">
                <input
                  id="mfa-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  required
                />
                <button className="btn btn-ghost" type="button" onClick={() => setCode(challenge.codeDemo)}>
                  Remplir
                </button>
              </div>
              <span className="hint">« Remplir » saisit le code automatiquement.</span>
            </div>
            {error && (
              <p className="alert alert-danger" role="alert">
                {error}
              </p>
            )}
            <div className="actions">
              <button className="btn" type="submit" disabled={busy || code.length !== 6}>
                {busy ? 'Vérification…' : 'Valider'}
              </button>
              <button className="btn btn-ghost" type="button" onClick={() => setChallenge(null)}>
                Retour
              </button>
            </div>
          </form>
        ) : (
          <form className="card stack" onSubmit={submitPassword} ref={formRef}>
            <div>
              <p className="eyebrow">Connexion</p>
              <h1 style={{ marginBottom: 0 }}>Accéder à mon espace</h1>
            </div>
            <div className="field">
              <label htmlFor="login-id">Identifiant</label>
              <input
                id="login-id"
                autoComplete="username"
                value={identifiant}
                onChange={(e) => setIdentifiant(e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="login-password">Mot de passe</label>
              <input
                id="login-password"
                type="password"
                autoComplete="current-password"
                value={motDePasse}
                onChange={(e) => setMotDePasse(e.target.value)}
                required
              />
            </div>
            {error && (
              <p className="alert alert-danger" role="alert">
                {error}
              </p>
            )}
            <button className="btn" type="submit" disabled={busy || !identifiant || !motDePasse}>
              {busy ? 'Connexion…' : 'Se connecter'}
            </button>
          </form>
        )}
        <aside className="card tint stack" style={{ gap: 12 }}>
          <span className="tile-icon" style={{ background: 'var(--surface)' }}>
            <Icon name="shield" />
          </span>
          <h3 style={{ margin: 0 }}>Connexion sécurisée</h3>
          <p className="muted" style={{ margin: 0 }}>
            Chaque connexion est tracée. Les agents confirment leur identité par un code de double
            authentification.
          </p>
        </aside>
      </div>

      <section className="stack" aria-labelledby="demo-accounts">
        <div>
          <h2 id="demo-accounts">Comptes</h2>
          <p className="muted small" style={{ margin: 0 }}>
            Cliquez sur un compte pour remplir l’identifiant et le mot de passe. Les comptes marqués « Double
            authentification » demandent ensuite un code.
          </p>
        </div>
        <div className="demo-accounts">
          {accounts.map((a) => (
            <button
              key={a.subject}
              type="button"
              className={`demo-account${a.identifiant === identifiant ? ' selected' : ''}`}
              onClick={() => choose(a)}
            >
              <span className="demo-account-name">{a.displayName.replace(/ \(démo\)$/, '')}</span>
              <span className="demo-account-desc">{a.description}</span>
              <span className="demo-account-meta">
                <code>{a.identifiant}</code>
                {a.doubleAuthentification && (
                  <span className="badge badge-info">Double authentification</span>
                )}
              </span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

export function OidcCallbackPage() {
  const { completeOidc } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    completeOidc()
      .then(() => navigate('/espace', { replace: true }))
      .catch(() => setError('La connexion n’a pas abouti. Réessayez.'));
  }, [completeOidc, navigate]);
  return error ? <p className="alert alert-danger">{error}</p> : <p>Connexion en cours…</p>;
}
